import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const API = axios.create({ baseURL: API_BASE });

// Attach auth token to every request
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Redirect to login on 401
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const loginUser = (username, password) =>
  API.post('/auth/login', { username, password });
export const verifyToken = () => API.get('/auth/me');

export const fetchDashboardSummary = () => API.get('/dashboard/summary');
export const fetchKPI = () => API.get('/dashboard/kpi');
export const fetchDistrictAvailability = (facType = '', extra = '') =>
  API.get(`/dashboard/district-availability${facType ? `?facType=${facType}` : (extra ? `?${extra}` : '')}`);
export const fetchFacilityTypeAvailability = () => API.get('/dashboard/facility-type-availability');
export const fetchFacilityDistribution = () => API.get('/dashboard/facility-distribution');
export const fetchDistrictRanking = (sort = 'desc', limit = 10) =>
  API.get(`/dashboard/district-ranking?sort=${sort}&limit=${limit}`);
export const fetchCategoryStock = () => API.get('/dashboard/category-stock');
export const fetchProcurementSummary = () => API.get('/dashboard/procurement-summary');
export const fetchDistricts = () => API.get('/districts');
export const fetchFacilities = () => API.get('/facilities');
export const fetchAlerts = () => API.get('/alerts');
export const fetchHmisSummary = () => API.get('/hmis/summary');
export const fetchHmisDrugAvailability = () => API.get('/hmis/drug-availability');
export const fetchHmisStockStats = () => API.get('/hmis/stock-stats');
export const fetchSku261 = () => API.get('/sku/261');
export const fetchSku352 = () => API.get('/sku/352');
export const fetchSkuProcurement = () => API.get('/sku/procurement');
export const fetchContacts = () => API.get('/contacts');

// Admin — Data upload & import
export const uploadStockFiles = (formData, onProgress) =>
  API.post('/admin/upload-stock', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  });
export const uploadConsumptionFiles = (formData, onProgress) =>
  API.post('/admin/upload-consumption', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  });
export const fetchUploadedFiles = () => API.get('/admin/uploaded-files');
export const clearUploads = () => API.delete('/admin/clear-uploads');
export const runImport = () => API.post('/admin/import', {}, { timeout: 600000 });

// Stockout tab endpoints
export const fetchStockoutDistricts = (sort = 'desc', limit = 10) =>
  API.get(`/dashboard/stockout-districts?sort=${sort}&limit=${limit}`);
export const fetchStockoutFacilityType = () => API.get('/dashboard/stockout-facility-type');
export const fetchStockoutFacilities = (sort = 'asc', limit = 10) =>
  API.get(`/dashboard/stockout-facilities?sort=${sort}&limit=${limit}`);
export const fetchStockoutCategories = () => API.get('/dashboard/stockout-categories');
export const fetchStockoutProducts = (limit = 10) =>
  API.get(`/dashboard/stockout-products?limit=${limit}`);

// Pipeline tab endpoints
export const fetchPipelineDistricts = () => API.get('/dashboard/pipeline-districts');
export const fetchPipelineSkus = () => API.get('/dashboard/pipeline-skus');

// Facility availability (for tab1)
export const fetchFacilityAvailability = () => API.get('/dashboard/facility-availability');

// District store-level detail (for map click drill-down)
export const fetchDistrictStores = (district) =>
  API.get(`/dashboard/district-stores?district=${encodeURIComponent(district)}`);

// DDWH item-level stock details (legacy)
export const fetchDdwhItems = (storeId, district) => {
  const params = storeId ? `storeId=${storeId}` : `district=${encodeURIComponent(district)}`;
  return API.get(`/dashboard/ddwh-items?${params}`);
};

// Generic facility item-level stock details (DDWH, MC, DH, SDH)
export const fetchFacilityItems = (facType, { storeId, district } = {}) => {
  let params = `facType=${facType}`;
  if (storeId) params += `&storeId=${storeId}`;
  else if (district) params += `&district=${encodeURIComponent(district)}`;
  return API.get(`/dashboard/facility-items?${params}`);
};

// Facility master counts
export const fetchFacilityMasterCounts = () => API.get('/dashboard/facility-master-counts');

export default API;
